import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { LifeBuoy, Plus, Inbox, ChevronRight } from 'lucide-react';
import { Button } from '@anyemp/ui-kit';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { ticketService } from '../../services/ticketService';
import { StatusBadge, PriorityDot, timeAgo } from './ticketBadges';
import TicketCreateModal from './TicketCreateModal';
import TicketDetailModal from './TicketDetailModal';
import { useRealtimeEvent } from '../../hooks/useRealtimeEvent';

/**
 * Embeddable "Related Tickets" widget. Shows the user's recent tickets for the
 * service and a button to file a new one (capturing the current page as source).
 */
export default function TicketsWidget({
  serviceName = 'talent-service',
  sourceLabel = '',
  limit = 5,
}) {
  const { hasPermission } = useAuth();
  const canListAll = hasPermission('support-service', 'ticket', 'list');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  // Default to actionable tickets only; closed/resolved live behind the toggle.
  const [showClosed, setShowClosed] = useState(false);
  // Privileged users (ticket:list) can flip the widget between their own
  // tickets and all tickets, in-place. Everyone else is locked to 'mine'.
  const [scope, setScope] = useState('mine');
  const effectiveScope = canListAll ? scope : 'mine';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { service: serviceName, scope: effectiveScope, limit };
      if (!showClosed) params.status = ['open', 'in_progress'];
      const body = await ticketService.list(params);
      setTickets(body?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [serviceName, limit, showClosed, effectiveScope]);

  useEffect(() => { load(); }, [load]);

  // Тикет мог создать кто угодно и из любого сервиса — подтягиваем список сразу,
  // пока окно открыто, без перезагрузки страницы и без опроса по таймеру
  useRealtimeEvent('support:ticket:created', load);

  return (
    <div className="overflow-hidden rounded-none border border-divider bg-surface-paper shadow-ui-card-elevated sm:rounded-ui-xl">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-divider bg-surface-default/40">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="grid place-items-center h-8 w-8 rounded-ui-md bg-primary-overlay text-primary shrink-0">
            <LifeBuoy size={16} />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-fg-primary leading-tight">
              {effectiveScope === 'all' ? 'All tickets' : 'Related tickets'}
            </h3>
            <p className="text-[11px] text-fg-tertiary leading-tight">
              {loading
                ? 'Loading…'
                : tickets.length
                  ? `${tickets.length} ${showClosed ? 'shown' : 'active'}`
                  : showClosed ? 'None yet' : 'None active'}
            </p>
          </div>
        </div>
        <Button size="small" color="primary" onClick={() => setCreating(true)} className="min-h-10 shrink-0">
          <Plus size={14} /> New
        </Button>
      </div>

      {/* Scope: Mine / All — only for users who can list all tickets. */}
      {canListAll && (
        <div className="px-4 pt-2.5">
          <div className="inline-flex rounded-ui-md border border-divider p-0.5 text-xs">
            {[['mine', 'Mine'], ['all', 'All']].map(([val, label]) => (
              <button
                key={val}
                type="button"
                onClick={() => setScope(val)}
                className={`min-h-10 px-3 py-2 rounded-ui-sm font-medium transition-colors
                  ${scope === val
                    ? 'bg-primary text-primary-contrast-text'
                    : 'text-fg-secondary hover:text-fg-primary'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Body */}
      <div className="px-2 py-2 max-h-[46vh] overflow-auto">
        {loading ? (
          <ul className="space-y-1.5 p-1">
            {[0, 1, 2].map((i) => (
              <li key={i} className="flex items-center gap-2 px-2 py-2 rounded-ui-md">
                <span className="h-2 w-2 rounded-full bg-action-hover" />
                <span className="h-3 w-16 rounded bg-action-hover" />
                <span className="h-3 flex-1 rounded bg-action-hover" />
              </li>
            ))}
          </ul>
        ) : tickets.length === 0 ? (
          <div className="flex flex-col items-center text-center gap-2 py-7 px-4">
            <span className="grid place-items-center h-10 w-10 rounded-full bg-surface-default text-fg-tertiary">
              <Inbox size={18} />
            </span>
            <p className="text-sm text-fg-secondary">
              {effectiveScope === 'all'
                ? (showClosed ? 'No tickets yet.' : 'No active tickets.')
                : (showClosed ? 'No tickets for this page yet.' : 'No active tickets for this page.')}
            </p>
            <Button size="small" variant="outlined" color="neutral" onClick={() => setCreating(true)}>
              <Plus size={14} /> Report an issue
            </Button>
          </div>
        ) : (
          <ul className="space-y-0.5">
            {tickets.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setOpenId(t.id)}
                  className="group grid min-h-11 w-full grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-2.5 rounded-ui-md px-2.5 py-2 text-left
                             hover:bg-action-hover transition-colors duration-ui-fast"
                >
                  <PriorityDot priority={t.priority} />
                  <div className="min-w-0">
                    <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="shrink-0 text-[11px] font-mono text-fg-tertiary">{t.ticket_number}</span>
                      <span className="min-w-0 break-words text-sm text-fg-primary">{t.title}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <StatusBadge status={t.status} />
                      <span className="text-[11px] tabular-nums text-fg-tertiary">{timeAgo(t.created_at)}</span>
                    </div>
                  </div>
                  <ChevronRight size={16} className="mt-2 text-fg-tertiary opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-divider">
        <button
          type="button"
          onClick={() => setShowClosed((v) => !v)}
          className="min-h-10 text-xs font-medium text-fg-secondary hover:text-fg-primary transition-colors"
        >
          {showClosed ? 'Hide closed' : 'Show closed'}
        </button>
        {canListAll && (
          <Link to="/support/tickets" className="inline-flex min-h-10 items-center gap-1 text-xs font-medium text-primary hover:gap-1.5 transition-all">
            View all <ChevronRight size={13} />
          </Link>
        )}
      </div>

      <TicketCreateModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={load}
        serviceName={serviceName}
        sourceLabel={sourceLabel}
      />
      <TicketDetailModal
        ticketId={openId}
        open={!!openId}
        onClose={() => setOpenId(null)}
        onChanged={load}
      />
    </div>
  );
}
